/**
 * The one module that talks to analytics and problem reporting. Umami (loaded by app.html, absent
 * on learner pages and in dev) gets the event so the session's recording can be found; the api
 * gets the full report so it survives even where Umami is blocked.
 */

type Umami = {
	track: (name: string, data?: Record<string, string | number | boolean>) => void
	identify: (id: string, data?: Record<string, string | number | boolean>) => void
}
const umami = () => (globalThis as unknown as { umami?: Umami }).umami

const API_URL = import.meta.env.VITE_API_URL ?? ''
const sent = new Set<string>()

// The account to link once Umami arrives: app.html inserts its script after load, so the app can
// know who she is before Umami exists. The latest one wins; app.html fires `temaly:umami` on load.
let pending: { id: string; guest: boolean } | null = null
if (typeof window !== 'undefined') {
	window.addEventListener('temaly:umami', () => {
		if (pending) umami()?.identify(pending.id, { guest: pending.guest })
		pending = null
	})
}

/** Links this visitor's sessions and recordings to her account. The id is opaque; no email. */
export function identifyVisitor(teacherId: string, guest: boolean): void {
	const u = umami()
	if (u) u.identify(teacherId, { guest })
	else pending = { id: teacherId, guest }
}

/** Once per message per page load: a render loop must not become a flood. */
export function reportProblem(
	message: string,
	detail?: string,
	/** The failed request, when there was one: its status and path, so reports group across languages. */
	request?: { status: number; route: string },
): void {
	if (sent.has(message) || sent.size > 50) return
	sent.add(message)
	const path = location.pathname + location.search
	umami()?.track('problem', { message: message.slice(0, 200), path })
	const body = JSON.stringify({
		message: message.slice(0, 2000),
		detail: detail?.slice(0, 8000),
		path,
		...request,
	})
	try {
		void fetch(`${API_URL}/api/problems`, {
			method: 'POST',
			headers: { 'content-type': 'application/json' },
			credentials: 'include',
			keepalive: true,
			body,
		}).catch(() => {})
	} catch {
		// Reporting is best effort; it must never throw into the code that hit the problem.
	}
}

const EXTENSION_NOISE =
	/window\.ethereum|selectedAddress|solana|tronLink|__gCrWeb|webkit\.messageHandlers/i
/** A frame of our own files: the built bundle, or Vite's source paths on the dev stack. */
const OWN_FRAME = /\/(assets|src)\//

/**
 * A crash an extension or an in-app browser caused, not our code: a wallet script injected into the
 * page (Brave, MetaMask) reports under the page's own address, so the file name alone cannot tell.
 * By its words, or by a stack that has frames and none of them ours. No stack proves nothing, so it
 * still reports. `api/src/features/problems/record.ts` holds the same rule for pages cached from
 * before; the two apps share no code.
 */
export function isForeignCrash(message: string, stack: string | undefined): boolean {
	if (EXTENSION_NOISE.test(message)) return true
	return !!stack && /https?:\/\//.test(stack) && !OWN_FRAME.test(stack)
}

/** Crashes nobody caught, including React render errors, which React 19 re-reports here. */
export function installProblemReporting(): void {
	window.addEventListener('error', (e) => {
		// A script from another origin (an extension, the analytics script) is not our problem.
		if (e.filename && !e.filename.startsWith(location.origin)) return
		// "Script error.": the browser hid a foreign script's error. Ours is same-origin and keeps its error.
		if (!e.filename && !e.error) return
		if (!e.error && /ResizeObserver loop/.test(e.message)) return
		const stack = e.error instanceof Error ? e.error.stack : undefined
		if (isForeignCrash(e.message, stack)) return
		reportProblem(`crash: ${e.message}`, stack)
	})
	window.addEventListener('unhandledrejection', (e) => {
		const r: unknown = e.reason
		// The browser skipped a page-change animation (phone keyboard or rotation resized the page); the page still changed.
		if (r instanceof DOMException && /transition/i.test(r.message)) return
		const message = r instanceof Error ? r.message : String(r)
		const stack = r instanceof Error ? r.stack : undefined
		if (isForeignCrash(message, stack)) return
		reportProblem(`unhandled: ${message}`, stack)
	})
}
